/**
 * 沖縄県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道・山形と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - タコライスを「1984年に〇〇で誕生」と断定しない。OCVBも「金武町が発祥とされる」。
 *  - 沖縄そばを「そば粉なしで“そば”を名乗れる唯一の例外」と断定しない。
 *    正確には1978年10月17日に「本場沖縄そば」として特殊名称登録・認証された経緯。
 *  - 沖縄そばの起源も説が複数ある。断定しない。
 *  - 「アグー」（琉球在来種）と「あぐー」（JAおきなわ銘柄豚）を混同しない。
 *    料理として出すので表示は「あぐー豚」にする。
 *  - ちんすこうの名の由来は二説。サーターアンダギーは中国の「開口笑」由来とされる。
 *  - 「琉球料理」に制度的な文化財指定があるかは確認できなかったので、制度名を付けない。
 *
 * 沖縄の歴史・文化について。
 *  琉球王国は1879年の琉球処分まで独立した王国だった。「昔から日本の一部だった」
 *  「日本の地方文化のひとつ」という前提で書かない。中国（明・清）との冊封・進貢関係が
 *  食文化の背景にあることは公式資料にも明記されている。
 *  戦後の米軍統治に由来する事物を、背景を抜きに「アメリカンでおしゃれ」と消費しない。
 *
 * 本番画像は public/assets/foods/okinawa/ に置く。
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { OKINAWA_FOOD_HULLS } from './okinawaFoodShapes.generated';
import { okinawaSceneryTour } from './okinawaScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'shikuwasa',
    level: 1,
    name: 'シークヮーサー',
    shortName: 'シークヮーサー',
    image: '/assets/foods/okinawa/01_shikuwasa.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '名前は「酸を食べさせるもの」。',
    source: 'https://www.vill.ogimi.okinawa.jp/sitekanri/gaiyo/profile/348.html',
    icon: { label: 'シークヮーサー', shape: 'berry', baseColor: '#7f9a48', accentColor: '#c8d84c' },
    reward: {
      unlockScore: 200,
      area: '大宜味村',
      headline: '「シー」は酸、「クヮーサー」は食べさせるの意味。沖縄を中心に自生する小さな柑橘。',
      tip: '青いうちは酸が強く、黄色く熟すと甘味と酸味が出る（12月から1月末ごろが食べごろ）。県内の収穫量は4,494トン（令和2年度）で、そのおよそ98パーセントが加工用。',
      links: searchLinks('大宜味村', 'シークヮーサー'),
    },
  },
  {
    id: 'chinsuko',
    level: 2,
    name: 'ちんすこう',
    shortName: 'ちんすこう',
    image: '/assets/foods/okinawa/02_chinsuko.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '冊封使をもてなすために生まれた菓子。',
    source: 'https://www.alic.go.jp/joho-s/joho07_002598.html',
    icon: { label: 'ちんすこう', shape: 'mochi', baseColor: '#e8d8b8', accentColor: '#c9a45a' },
    reward: {
      unlockScore: 500,
      area: '沖縄県',
      headline: '19世紀の琉球王国時代、王家の料理人・新垣淑規が、冊封使をもてなすために中国菓子を学んだのが起こりとされる。',
      tip: '名前の由来は「珍楚こう（珍しい菓子）」説と「金楚こう（王族・貴族だけが口にできる高価な食べ物）」説の二つがある。',
      links: searchLinks('沖縄県', 'ちんすこう'),
    },
  },
  {
    id: 'sata-andagi',
    level: 3,
    name: 'サーターアンダギー',
    shortName: 'サーターアンダギー',
    image: '/assets/foods/okinawa/03_sata_andagi.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '揚げると、割れ目が花のように開く。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/47_8_okinawa.html',
    icon: { label: 'アンダギー', shape: 'dumpling', baseColor: '#d8a45c', accentColor: '#8c5a24' },
    reward: {
      unlockScore: 1000,
      area: '沖縄県',
      headline: '「サーター」は砂糖、「アンダーギー」は揚げる。小麦粉を卵・砂糖と合わせてこね、油で揚げた沖縄風のドーナツ。',
      tip: '揚げるとチューリップの花が咲いたような形に割れる。中国から伝わった「開口笑」「開口球」に似た菓子と考えられていて、およそ500年前に王家の料理人が中国からレシピを持ち帰ったのが起こりとされる。',
      links: searchLinks('沖縄県', 'サーターアンダギー'),
    },
  },
  {
    id: 'jimami-dofu',
    level: 4,
    name: 'ジーマーミ豆腐',
    shortName: 'ジーマーミ豆腐',
    image: '/assets/foods/okinawa/04_jimami_dofu.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '大豆ではなく、落花生からつくる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/47_18_okinawa.html',
    icon: { label: 'ジーマーミ', shape: 'mochi', baseColor: '#e8dcc0', accentColor: '#b59a68' },
    reward: {
      unlockScore: 1800,
      area: '沖縄県',
      headline: '「ジーマーミ」は、土の中に豆ができる落花生＝「地豆」のこと。豆腐という名だが大豆は使わない。',
      tip: '生の落花生の絞り汁にいもくずを混ぜて加熱して作る。琉球王朝時代から落花生は貴重な食材で、ハレの日に用いられていた。',
      links: searchLinks('沖縄県', 'ジーマーミ豆腐'),
    },
  },
  {
    id: 'mimiga',
    level: 5,
    name: 'ミミガー',
    shortName: 'ミミガー',
    image: '/assets/foods/okinawa/05_mimiga.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '豚の耳を、千切りにして和える。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/47_15_okinawa.html',
    icon: { label: 'ミミガー', shape: 'roe', baseColor: '#e8c8c0', accentColor: '#9a6a5a' },
    reward: {
      unlockScore: 3000,
      area: '沖縄県',
      headline: '「ミミガーは豚の耳、刺身は和え物のこと」。毛を落とすために直火であぶり、加熱してから千切りにする。',
      tip: '沖縄では豚を「鳴き声以外すべて食べる」といわれる。耳を使う料理は県外ではほとんど出回らない。軟骨を含むこりこりした食感。',
      links: searchLinks('沖縄県', 'ミミガー'),
    },
  },
  {
    id: 'umibudo',
    level: 6,
    name: '海ぶどう',
    shortName: '海ぶどう',
    image: '/assets/foods/okinawa/06_umibudo.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '口のなかで、ぷちぷちとはじける。',
    source: 'https://www.vill.onna.okinawa.jp/sp/about/summary/1484721632/1484721927/',
    icon: { label: '海ぶどう', shape: 'roe', baseColor: '#7fae4c', accentColor: '#3f6b28' },
    reward: {
      unlockScore: 4500,
      area: '恩納村・うるま市',
      headline: '標準和名はクビレズタ。ぶどうのように小さな実がついた緑色の海藻で、陸上で養殖される。',
      tip: '沖縄県の養殖生産量・生産額ともに第3位。北部では伊是名・国頭・東・大宜味・名護・本部・宜野座・金武・恩納で育てられている。',
      links: searchLinks('恩納村', '海ぶどう'),
    },
  },
  {
    id: 'taco-rice',
    level: 7,
    name: 'タコライス',
    shortName: 'タコライス',
    image: '/assets/foods/okinawa/07_taco_rice.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'ご飯の上に、タコスの具。',
    source: 'https://www.okinawastory.jp/news/tourism/4654',
    icon: { label: 'タコライス', shape: 'curry-plate', baseColor: '#c1442f', accentColor: '#f3e6c6' },
    reward: {
      unlockScore: 6500,
      area: '金武町',
      headline: '沖縄観光コンベンションビューローの公式サイトは「金武町が発祥とされるご当地グルメ」と紹介している。',
      tip: '金武町の公式観光ポータルには「タコライスを産んだ新開地エリア」という項目があり、米軍基地前の新開地が発祥のエリアとされている。',
      links: searchLinks('金武町', 'タコライス'),
    },
  },
  {
    id: 'goya-champuru',
    level: 8,
    name: 'ゴーヤーチャンプルー',
    shortName: 'ゴーヤーチャンプルー',
    image: '/assets/foods/okinawa/08_goya_champuru.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '島豆腐と、季節の野菜の炒め物。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/47_11_okinawa.html',
    icon: { label: 'チャンプルー', shape: 'curry-plate', baseColor: '#5f8a3a', accentColor: '#f0e6cf' },
    reward: {
      unlockScore: 9000,
      area: '沖縄県',
      headline: '「チャンプルー」は、焼いた島豆腐と季節の野菜の炒め物のこと。',
      tip: '沖縄の豆腐は堅くしっかりしていて、できたてを熱いまま水にさらさずに売るのでうま味が残る。ゴーヤーの苦みは食欲を刺激し、熱に強いビタミンCを多く含むことから夏バテ予防に重宝されてきた。',
      links: searchLinks('沖縄県', 'ゴーヤーチャンプルー'),
    },
  },
  {
    id: 'okinawa-soba',
    level: 9,
    name: '沖縄そば',
    shortName: '沖縄そば',
    image: '/assets/foods/okinawa/09_okinawa_soba.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'そば粉は一切使わない、小麦粉100パーセント。',
    source: 'https://oki-soba.jp/history/day/',
    icon: { label: '沖縄そば', shape: 'noodle-bowl', baseColor: '#e8d8b0', accentColor: '#8a5a32' },
    reward: {
      unlockScore: 12000,
      area: '沖縄県',
      headline: '生めん類の表示に関する公正競争規約では、そば粉三割以上でないと「そば」と表示できない。交渉の末、1978年10月17日に「本場沖縄そば」として特殊名称の登録が認証された。',
      tip: '沖縄生麺協同組合は1997年からこの日を「沖縄そばの日」としている。「本場沖縄そば」には、沖縄県内で製造すること、めん線をゆでる前に必ず手もみすること、油処理してあることなど、細かい基準がある。',
      links: searchLinks('沖縄県', '沖縄そば'),
    },
  },
  {
    id: 'soki-soba',
    level: 10,
    name: 'ソーキそば',
    shortName: 'ソーキそば',
    image: '/assets/foods/okinawa/10_soki_soba.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '骨から肉が外れるまで煮た、あばら肉をのせて。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/47_20_okinawa.html',
    icon: { label: 'ソーキ', shape: 'ramen', baseColor: '#8a5a32', accentColor: '#e8d8b0' },
    reward: {
      unlockScore: 16000,
      area: '沖縄県',
      headline: '「ソーキ」は豚の骨付きあばら肉。脂肪と肉のバランスがよく、香りも立つ。',
      tip: '骨から肉が外れるほど柔らかく煮るのがコツ。農林水産省は沖縄そばの項で「近年はソーキそばや野菜そばなど多様化しています」と記している。',
      links: searchLinks('沖縄県', 'ソーキそば'),
    },
  },
  {
    id: 'rafute',
    level: 11,
    name: 'ラフテー',
    shortName: 'ラフテー',
    image: '/assets/foods/okinawa/11_rafute.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '皮つき三枚肉を、泡盛で6時間。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/47_6_okinawa.html',
    icon: { label: 'ラフテー', shape: 'curry-plate', baseColor: '#6b3a22', accentColor: '#e8d8b8' },
    reward: {
      unlockScore: 22000,
      area: '沖縄県',
      headline: '皮つきの豚三枚肉の角煮。砂糖・醤油・泡盛で気長に煮込む。',
      tip: '全体で6〜7時間煮るのが理想で、皮が箸で切れる柔らかさになったら出来上がり。もとは保存食だったが、いまは客膳料理や法事料理として振る舞われる。',
      links: searchLinks('沖縄県', 'ラフテー'),
    },
  },
  {
    id: 'agu-shabushabu',
    level: 12,
    name: 'あぐー豚のしゃぶしゃぶ',
    shortName: 'あぐー豚',
    image: '/assets/foods/okinawa/12_agu_shabushabu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '600年以上前に、中国から琉球へ渡ってきたとされる豚。',
    source: 'https://agu-pr.jp/aboutagu/',
    icon: { label: 'あぐー', shape: 'hotpot', baseColor: '#e8b0a8', accentColor: '#f4efe4' },
    reward: {
      unlockScore: 30000,
      area: '沖縄県',
      headline: '琉球在来種の純血種は「アグー」、JAおきなわの銘柄豚の商品名は「あぐー」で、アグーと西洋豚から生まれた肉のこと。表記で意味が変わる。',
      tip: '600年以上前に中国から琉球王国へ来たとされる。「沖縄アグー豚」は協議会の証明規程にもとづき、県内で飼養され、生年月日と血統が明らかで、雌豚のミトコンドリアDNAが東洋タイプであるなどの基準を満たしたものだけが登録される。',
      links: searchLinks('沖縄県', 'あぐー豚 しゃぶしゃぶ'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const okinawaFoodStage: StageDefinition = {
  regionId: 'okinawa',
  regionName: '沖縄県',
  mode: 'food',
  title: '沖縄めしマージ',
  theme: {
    primaryColor: '#1f8a8a',
    secondaryColor: '#e8a13c',
    accentColor: '#f5d873',
    backgroundColor: '#131c1e',
  },
  /** 背景ツアー「沖縄めぐり」。軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: okinawaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, OKINAWA_FOOD_HULLS),
};
